import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LogoAtivaJa from "@/components/LogoAtivaJa";
import CadastroEmAnalise from "@/components/CadastroEmAnalise";
import { EmpresaOnboardingGate } from "@/components/EmpresaOnboardingGate";
import {
  LayoutDashboard, Briefcase, Wrench, PackageOpen, ClipboardCheck,
  Building2, Users, UserCog,
  TrendingUp, FileBarChart, Activity, Brain, Sparkles, BookOpen,
  Receipt, FileText, Calendar, Wallet, ScrollText,
  ShieldCheck, FileSignature, GitBranch, Megaphone, ShoppingBag,
  MessageSquare, BellRing, Star, Handshake, Clock,
  Settings, History, HardDrive,
  LogOut, ChevronDown, Search, AlertTriangle, MapPin, Menu, X, UserCheck, Trash2, MonitorPlay
} from "lucide-react";

// ===== Seções do Sidebar — agrupadas com cor laranja no label =====
const SECTIONS = [
  // ADMIN MASTER (plataforma: rede, assinaturas, auditoria, sistema)
  { group: "Dashboard", admin_only: true, items: [
    { to: "/admin", label: "Visão SaaS", icon: LayoutDashboard, roles: ["admin"], end: true },
  ]},
  // OPERAÇÃO (Empresa/Montadora — Admin não opera)
  { group: "Operação", items: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["empresa","montadora"], end: true },
    { to: "/admin/servicos", label: "Serviços", icon: Briefcase, roles: ["empresa","montadora"] },
    { to: "/admin/assistencias", label: "Assistências", icon: Wrench, roles: ["empresa","montadora"] },
    { to: "/admin/avaliacoes-pos-montagem", label: "Avaliação Pós-Montagem", icon: ClipboardCheck, roles: ["empresa","montadora","admin"] },
    { to: "/admin/garantias", label: "Garantias", icon: ShieldCheck, roles: ["empresa","admin"] },
    { to: "/admin/manuais", label: "Manuais de Produto", icon: BookOpen, roles: ["empresa", "montadora", "admin"] },
    { to: "/admin/pulmao", label: "Pulmão de Peças", icon: PackageOpen, roles: ["empresa","montadora"] },
  ]},
  // MINHA EMPRESA (auto-gestão da empresa)
  { group: "Minha Empresa", items: [
    { to: "/admin/minha-empresa", label: "Dados & Nichos", icon: Building2, roles: ["empresa"] },
    { to: "/admin/equipe", label: "Minha Equipe", icon: Users, roles: ["empresa", "montadora"], dono_only: true },
  ]},
  // REDE
  { group: "Rede", items: [
    { to: "/admin/empresas", label: "Empresas", icon: Building2, roles: ["admin"] },
    { to: "/admin/montadoras", label: "Montadoras", icon: Users, roles: ["admin","montadora"] },
    { to: "/admin/montadores", label: "Montadores", icon: UserCog, roles: ["admin","montadora"] },
    { to: "/admin/catalogo-tempos", label: "Catálogo de Tempos", icon: Clock, roles: ["admin"] },
    { to: "/admin/alertas-operacionais", label: "Alertas Operacionais", icon: AlertTriangle, roles: ["admin","empresa","montadora"] },
    { to: "/admin/pesquisar-montadoras", label: "Pesquisar Montadoras", icon: Search, roles: ["empresa"] },
    { to: "/admin/parcerias", label: "Parcerias", icon: Handshake, roles: ["empresa","montadora","admin"] },
    { to: "/admin/alertas-desempenho", label: "Alertas de Desempenho", icon: AlertTriangle, roles: ["empresa"] },
    { to: "/admin/regioes-atendimento", label: "Regiões Atendidas", icon: MapPin, roles: ["montadora"] },
    { to: "/admin/ocorrencias-montadora", label: "Ocorrências de Montadora", icon: AlertTriangle, roles: ["empresa","montadora","admin"] },
    { to: "/admin/cadastros-pendentes", label: "Cadastros Pendentes", icon: UserCheck, roles: ["admin"] },
    { to: "/admin/atividade-rede", label: "Atividade da Rede", icon: Activity, roles: ["admin"] },
    { to: "/admin/leads", label: "Leads de Demonstração", icon: Megaphone, roles: ["admin"] },
    { to: "/admin/limpeza-teste", label: "Limpeza de Testes", icon: Trash2, roles: ["admin"] },
  ]},
  // INTELIGÊNCIA
  { group: "Inteligência", items: [
    { to: "/admin/volumetria", label: "Volumetria", icon: TrendingUp, roles: ["empresa"] },
    { to: "/admin/agregado-montadores", label: "Agregado de Montadores", icon: Users, roles: ["empresa"] },
    { to: "/admin/insights", label: "Insights", icon: Brain, roles: ["empresa","montadora"] },
    { to: "/admin/avaliacoes", label: "Avaliações dos Clientes", icon: Star, roles: ["empresa","montadora","admin"] },
    { to: "/admin/relatorios", label: "Relatórios", icon: FileBarChart, roles: ["admin","empresa"] },
    { to: "/admin/casos-sucesso", label: "Casos de Sucesso", icon: Activity, roles: ["empresa","montadora"] },
    { to: "/admin/avaliacoes-plataforma", label: "Avaliações da Rede", icon: Star, roles: ["admin"] },
  ]},
  // IMPORTAÇÕES
  { group: "Importações", items: [
    { to: "/admin/importar", label: "Importar Planilha", icon: ShoppingBag, roles: ["empresa"] },
    { to: "/admin/cliente-particular", label: "Cliente Particular", icon: Sparkles, roles: ["empresa", "montadora", "admin"] },
    { to: "/admin/importar", label: "Modelos & Histórico", icon: ShoppingBag, roles: ["admin"] },
  ]},
  // FINANCEIRO (operacional da empresa)
  { group: "Financeiro", items: [
    { to: "/admin/financeiro", label: "Financeiro", icon: Wallet, roles: ["empresa"] },
    { to: "/admin/notas-fiscais", label: "Notas Fiscais", icon: Receipt, roles: ["empresa"] },
    { to: "/admin/valores-servicos", label: "Tabela de Valores", icon: ScrollText, roles: ["empresa"] },
    { to: "/admin/calendario-fechamento", label: "Calendário de Fechamento", icon: Calendar, roles: ["empresa"] },
    { to: "/admin/config-fiscal", label: "Configuração Fiscal", icon: FileText, roles: ["empresa"] },
  ]},
  // FINANCEIRO SAAS (plataforma)
  { group: "Financeiro SaaS", items: [
    { to: "/admin/assinaturas", label: "Assinaturas SaaS", icon: FileSignature, roles: ["admin"] },
    { to: "/admin/faturamento-saas", label: "Faturamento SaaS", icon: Wallet, roles: ["admin"] },
    { to: "/admin/minhas-faturas", label: "Minhas Faturas", icon: Receipt, roles: ["empresa","montadora"] },
    { to: "/admin/valores-empresas", label: "Valores das Empresas", icon: Wallet, roles: ["montadora"] },
    { to: "/admin/conexoes-particulares", label: "Conexões Particulares", icon: Wallet, roles: ["admin"] },
    { to: "/admin/financeiro-montadora", label: "Repasses Montadora", icon: Wallet, roles: ["admin","montadora"] },
    { to: "/admin/extrato-montadora", label: "A Receber", icon: Wallet, roles: ["montadora"] },
    { to: "/admin/extrato-taxa", label: "Taxa MontaJá (5%)", icon: Wallet, roles: ["montadora"] },
  ]},
  // COMPLIANCE
  { group: "Compliance", items: [
    { to: "/admin/comunicados", label: "Comunicados Formais", icon: Megaphone, roles: ["empresa","admin"] },
    { to: "/admin/aditivos", label: "Aditivos Contratuais", icon: FileSignature, roles: ["empresa"] },
    { to: "/admin/contratos-montador", label: "Contratos de Montadores", icon: FileSignature, roles: ["empresa"] },
    { to: "/admin/planos-acao", label: "Planos de Ação", icon: GitBranch, roles: ["admin","empresa","montadora"] },
    { to: "/admin/auditoria-juridica", label: "Contratos & Dossiês", icon: ShieldCheck, roles: ["admin"] },
    { to: "/admin/auditoria", label: "Auditoria Técnica", icon: History, roles: ["admin"] },
  ]},
  // COMERCIAL
  { group: "Comercial", items: [
    { to: "/admin/campanhas", label: "Campanhas de Incentivo", icon: Megaphone, roles: ["empresa","montadora"] },
    { to: "/admin/condicoes", label: "Publicar Condições", icon: Megaphone, roles: ["empresa"] },
    { to: "/admin/condicoes-empresas", label: "Condições de Empresas", icon: FileSignature, roles: ["montadora"] },
    { to: "/admin/referencias-valores", label: "Referências de Valores", icon: FileSignature, roles: ["admin"] },
    { to: "/admin/showroom", label: "Showroom de Vendas", icon: MonitorPlay, roles: ["admin"] },
  ]},
  // COMUNICAÇÃO
  { group: "Comunicação", items: [
    { to: "/admin/mensagens-whatsapp", label: "Respostas WhatsApp", icon: MessageSquare, roles: ["admin","empresa","montadora"] },
    { to: "/admin/chat", label: "Chat Interno", icon: MessageSquare, roles: ["empresa","montadora"] },
    { to: "/admin/chat", label: "Conversas Master", icon: MessageSquare, roles: ["admin"] },
  ]},
  // SISTEMA
  { group: "Sistema", items: [
    { to: "/admin/configuracoes", label: "Configurações", icon: Settings, roles: ["admin"] },
    { to: "/admin/backups", label: "Backups & MEI", icon: HardDrive, roles: ["admin"] },
  ]},
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const basePrefix = user?.role === "empresa" ? "/empresa" : user?.role === "montadora" ? "/montadora" : "/admin";
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (user && ["empresa", "montadora"].includes(user.role) &&
      user.aprovacao_status && user.aprovacao_status !== "aprovada") {
    return <CadastroEmAnalise />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const FIN_ALLOW = ["/admin/minhas-faturas", "/admin/financeiro", "/admin/notas-fiscais",
    "/admin/extrato-montadora", "/admin/extrato-taxa", "/admin/financeiro-montadora"];
  const OP_BLOCK = ["/admin/minhas-faturas", "/admin/financeiro", "/admin/notas-fiscais",
    "/admin/valores-servicos", "/admin/calendario-fechamento", "/admin/config-fiscal",
    "/admin/minha-empresa", "/admin/equipe", "/admin/extrato-montadora", "/admin/extrato-taxa",
    "/admin/financeiro-montadora", "/admin/condicoes", "/admin/aditivos", "/admin/contratos-montador"];
  const permUser = user?.subconta ? user?.permissao : "dono";
  const pathCanon = location.pathname.replace(/^\/(empresa|montadora)/, "/admin");
  const rotaPermitida = (() => {
    if (!["empresa", "montadora"].includes(user?.role) || permUser === "dono") return true;
    if (permUser === "financeiro") {
      return FIN_ALLOW.some(p => pathCanon === p || pathCanon.startsWith(p + "/"));
    }
    if (permUser === "operacional") {
      return !OP_BLOCK.some(p => pathCanon === p || pathCanon.startsWith(p + "/"));
    }
    return true;
  })();
  const passaPermissao = (i) => {
    if (!["empresa", "montadora"].includes(user?.role)) return true;
    if (i.dono_only && permUser !== "dono") return false;
    if (permUser === "financeiro") return FIN_ALLOW.includes(i.to);
    if (permUser === "operacional") return !OP_BLOCK.includes(i.to);
    return true;
  };
  const visibleSections = SECTIONS
    .map(s => ({ ...s, items: s.items.filter(i => i.roles.includes(user?.role) && passaPermissao(i)) }))
    .filter(s => s.items.length > 0);

  const roleLabel = (user?.role === "admin" ? "Admin Master"
    : user?.role === "empresa" ? "Empresa"
    : user?.role === "montadora" ? "Montadora"
    : "Painel") + (user?.subconta && user?.permissao ? ` · ${user.permissao.toUpperCase()}` : "");

  const roleBadgeClass = user?.role === "admin"
    ? "bg-aj-blue-deep text-white"
    : user?.role === "empresa"
    ? "bg-gradient-to-r from-[#FF6700] to-[#F97316] text-white"
    : "bg-emerald-600 text-white";

  return (
    <div className="min-h-screen bg-aj-surface lg:grid lg:grid-cols-[260px_1fr] font-body">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-overlay" />
      )}
      {/* Sidebar */}
      <aside className={`bg-white border-r border-aj-border flex flex-col h-screen w-[260px] max-w-[80vw]
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-200
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:static lg:translate-x-0 lg:sticky lg:top-0 lg:z-auto`}>
        <div className="px-5 py-5 border-b border-aj-border flex items-start justify-between">
          <div>
            <LogoAtivaJa size="md" />
            <div className={`mt-2 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${roleBadgeClass}`} data-testid="role-badge">
              {roleLabel}
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 -mr-1 text-aj-text-muted"
            data-testid="admin-sidebar-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {visibleSections.map((sec) => (
            <div key={sec.group} className="mb-2">
              <div className="px-5 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
                {sec.group}
              </div>
              {sec.items.map((n) => (
                <NavLink key={n.to + n.label} to={n.to.replace(/^\/admin/, basePrefix)} end={n.end}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[&]/g, "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2 text-sm font-medium transition-all border-l-2 ${
                      isActive
                        ? "bg-aj-amber-soft text-aj-orange-hover border-aj-orange font-semibold"
                        : "text-aj-text border-transparent hover:bg-aj-surface hover:text-aj-blue-deep"
                    }`}>
                  {n.icon && <n.icon className="w-4 h-4 flex-shrink-0" />}
                  <span className="truncate">{n.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User card no rodapé */}
        <div className="p-4 border-t border-aj-border bg-aj-surface/50">
          <button onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white transition"
            data-testid="admin-user-menu">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-aj-blue-dark to-aj-blue-deep text-white font-bold text-xs flex items-center justify-center">
              {(user?.name || user?.email || "U").substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-xs font-semibold text-aj-blue-deep truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] text-aj-text-muted truncate">{user?.email}</div>
            </div>
            <ChevronDown className={`w-4 h-4 text-aj-text-muted transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {userMenuOpen && (
            <button onClick={handleLogout}
              className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-xs font-semibold text-aj-danger hover:bg-red-50 rounded transition"
              data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> Sair da plataforma
            </button>
          )}
        </div>
      </aside>

      {/* Conteúdo */}
      <main className="overflow-auto min-h-screen">
        <TopBar user={user} roleLabel={roleLabel} onMenu={() => setSidebarOpen(true)} />
        <EmpresaOnboardingGate />
        {user?.role === "admin" && <PendenciasBanner />}
        {(user?.role === "empresa" || user?.role === "montadora") && <OsParadasBanner />}
        <div className="p-4 sm:p-6 lg:p-8">
          {!rotaPermitida && permUser === "financeiro" && pathCanon === "/admin" ? (
            <Navigate to={`${basePrefix}/minhas-faturas`} replace />
          ) : !rotaPermitida ? (
            <div className="max-w-md mx-auto mt-16 border border-border bg-white p-8 text-center" data-testid="sem-acesso-permissao">
              <div className="text-4xl mb-3">🔒</div>
              <div className="font-heading font-bold text-lg">Sem acesso a esta área</div>
              <p className="text-sm text-muted-foreground mt-2">
                Sua permissão de equipe ({permUser}) não inclui esta tela. Fale com o dono da conta se precisar de acesso.
              </p>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}

function OsParadasBanner() {
  const [p, setP] = useState(null);
  useEffect(() => {
    const load = () => api.get("/operacao/os-paradas").then(r => setP(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);
  if (!p || !p.total) return null;
  return (
    <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 rounded-lg border-2 border-[#E60000] bg-red-50 p-4 flex items-center justify-between gap-3 flex-wrap"
      data-testid="os-paradas-banner">
      <div className="flex items-center gap-3 min-w-0">
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E60000] opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#E60000]" />
        </span>
        <div className="text-sm">
          <strong>{p.total} OS sem montador</strong> aguardando aceite —{" "}
          {p.os.slice(0, 3).map(o => o.pim_code).join(", ")}{p.total > 3 && "…"}. Você pode redistribuir pelo painel.
        </div>
      </div>
      <Link to="/admin/servicos" className="shrink-0 px-4 py-2 rounded-md bg-[#E60000] hover:opacity-90 text-white text-xs font-bold uppercase tracking-wider"
        data-testid="os-paradas-ver">Ver serviços →</Link>
    </div>
  );
}

function PendenciasBanner() {
  const [p, setP] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/pendencias-liberacao").then(r => setP(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("pendencias-atualizadas", load);
    return () => { clearInterval(t); window.removeEventListener("pendencias-atualizadas", load); };
  }, []);
  if (!p || !p.total) return null;
  const partes = [
    p.empresas > 0 && `${p.empresas} Empresa(s)`,
    p.montadoras > 0 && `${p.montadoras} Montadora(s)`,
    p.montadores > 0 && `${p.montadores} Montador(es)`,
  ].filter(Boolean).join(" · ");
  return (
    <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 rounded-lg border-2 border-[#FF6700] bg-[#FFF4EC] p-4 flex items-center justify-between gap-3 flex-wrap"
      data-testid="pendencias-banner">
      <div className="flex items-center gap-3 min-w-0">
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF6700] opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF6700]" />
        </span>
        <div className="text-sm">
          <strong className="text-aj-blue-deep">Liberações aguardando você:</strong>{" "}
          {partes && <span>{partes} para aprovar cadastro</span>}
          {partes && p.conferencia_documental > 0 && " · "}
          {p.conferencia_documental > 0 && <span>{p.conferencia_documental} Empresa(s) com documentação para conferir</span>}
        </div>
      </div>
      <Link to="/admin/cadastros-pendentes"
        className="shrink-0 px-4 py-2 rounded-md bg-[#FF6700] hover:bg-[#e05a00] text-white text-xs font-bold uppercase tracking-wider"
        data-testid="pendencias-banner-acao">
        Analisar agora →
      </Link>
    </div>
  );
}

function TopBar({ user, roleLabel, onMenu }) {
  return (
    <div className="sticky top-0 z-30 bg-white border-b border-aj-border px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={onMenu}
          className="lg:hidden w-9 h-9 rounded-lg bg-aj-surface hover:bg-aj-amber-soft transition flex items-center justify-center flex-shrink-0"
          data-testid="admin-menu-toggle">
          <Menu className="w-5 h-5 text-aj-blue-deep" />
        </button>
        <div className="min-w-0">
          <div className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
            MontaJá · {roleLabel}
          </div>
          <div className="text-sm font-display font-bold text-aj-blue-deep truncate">
            Bem-vindo, {(user?.name || user?.email || "").split(" ")[0] || "operador"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <button className="hidden sm:flex w-9 h-9 rounded-lg bg-aj-surface hover:bg-aj-amber-soft transition items-center justify-center" data-testid="topbar-search">
          <Search className="w-4 h-4 text-aj-text-muted" />
        </button>
        <button className="relative w-9 h-9 rounded-lg bg-aj-surface hover:bg-aj-amber-soft transition flex items-center justify-center" data-testid="topbar-notif">
          <BellRing className="w-4 h-4 text-aj-text-muted" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-aj-orange" />
        </button>
      </div>
    </div>
  );
}
